import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import type { WorkspaceWithRole } from '@/types'
import { api } from '@/lib/api'

interface WorkspaceContextValue {
  workspaces: WorkspaceWithRole[]
  activeWorkspace: WorkspaceWithRole | null
  setActiveWorkspace: (workspace: WorkspaceWithRole | null) => void
  loading: boolean
  reload: () => void
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn } = useAuth()
  const [workspaces, setWorkspaces] = useState<WorkspaceWithRole[]>([])
  const [activeWorkspace, setActiveWorkspaceState] = useState<WorkspaceWithRole | null>(null)
  const [loading, setLoading] = useState(false)
  const [rev, setRev] = useState(0)

  const reload = useCallback(() => setRev((r) => r + 1), [])

  useEffect(() => {
    if (!isSignedIn) {
      setWorkspaces([])
      setActiveWorkspaceState(null)
      return
    }

    let cancelled = false
    setLoading(true)

    async function load() {
      const token = await getToken()
      if (!token || cancelled) return
      try {
        const data = await api.workspaces.list(token)
        if (cancelled) return
        setWorkspaces(data.workspaces)
        // Restore previously selected workspace, or default to first
        setActiveWorkspaceState((prev) => {
          if (prev) {
            const found = data.workspaces.find((w) => w.id === prev.id)
            return found ?? data.workspaces[0] ?? null
          }
          return data.workspaces[0] ?? null
        })
      } catch {
        // Fail silently — workspace list is non-critical
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, rev])

  const setActiveWorkspace = useCallback((ws: WorkspaceWithRole | null) => {
    setActiveWorkspaceState(ws)
  }, [])

  return (
    <WorkspaceContext.Provider value={{ workspaces, activeWorkspace, setActiveWorkspace, loading, reload }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider')
  return ctx
}
